import * as R from "remeda";
import { v4 as uuidv4 } from 'uuid';
import { useEffect, useRef, useState } from "react";
import { ContextInputField, ContextInputPage } from "./ContextInput";
import { usePostApiChatChat } from "@/API";
import { useImmer } from "use-immer";
import { ChatModel } from "./ChatResultModel";
import { ActionIcon, Button, Group, Modal, ScrollArea, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { ChatRequestCard } from "./ChatCard";
import { notifications } from "@mantine/notifications";
import { AxiosError } from "axios";
import { BaseInputField, BaseInputPage } from "./BaseInput";
import { IconSend2 } from "@tabler/icons-react";
import { FeedbackInputPage } from "./FeedbackInput";

const ChatStep = {
    基本資料: 0,
    聊天: 1,
    回饋資料: 2,
} as const;

export const infoUrl = "https://xn--xmq18l26bhy4c6pi0ua.com/"

const limited = 5;

export function ChatBot() {
    const [step, setStep] = useState<typeof ChatStep[keyof typeof ChatStep]>(ChatStep.基本資料);
    // const [step, setStep] = useState<typeof ChatStep[keyof typeof ChatStep]>(ChatStep.聊天);
    const maxHistoryRecordsCount = 6
    const [context, setContext] = useState<ContextInputField>()
    const [baseContext, setBaseContext] = useState<BaseInputField>()
    const [used, setUsed] = useState(0);
    const [opened, setOpened] = useState(true);
    const [waiting, setWaiting] = useState(false);
    const [chatResults, setChatResults] = useImmer<ChatModel[]>([]);
    //已達提問上限
    const hasReachedLimit = used >= limited;
    const [openhasReachedLimit, setOpenhasReachedLimit] = useState(false);

    const Chat_Mutation = usePostApiChatChat()
    const viewport = useRef<HTMLDivElement>(null);
    const scrollToBottom = () =>
        viewport.current!.scrollTo({ top: viewport.current!.scrollHeight, behavior: 'smooth' });
    const form = useForm({
        initialValues: {
            text: '',
        }
    });

    function GetRecords(beforeIndex: number | undefined = undefined) {
        var result = R.flatMap(chatResults.slice(0, beforeIndex), x => {
            return [...x.response.records ?? []]
        })
        var output = []
        var counter = 0;
        for (var i = result.length - 1; i >= 0; i--) {
            if (result[i].role == "Assistant" || result[i].role == "User") {
                counter++;
                if (counter > maxHistoryRecordsCount) break;
            }
            output.push(result[i])
        }
        return output.reverse();
    }

    async function EditResult(result: ChatModel) {
        if (hasReachedLimit) return;
        setWaiting(true)
        try {
            var index = chatResults.findIndex(x => x.id == result.id)
            var records = GetRecords(index)
            setChatResults(s => {
                var index = s.findIndex(x => x.id == result.id)
                if (index < 0) return s;
                var newData = s.slice(0, index)
                var addData = { ...result }
                addData.responseLoading = true
                addData.response = {}
                addData.responseFail = false
                addData.responseTime = 0
                newData.push(addData)
                return newData
            })
            const start = Date.now()
            var response = await Chat_Mutation.mutateAsync({
                data: {
                    message: result.requestText,
                    records: records,
                }
            })
            const finish = Date.now()
            const time = (finish - start) / 1000
            setChatResults(s => {
                var index = s.findIndex(x => x.id == result.id)
                if (index >= 0) {
                    s[index].response = response.data ?? {}
                    s[index].responseTime = time
                    s[index].responseLoading = false
                }
            })
            scrollToBottom()
        } catch (e) {
            var error = e as AxiosError
            notifications.show({ message: "傳送失敗", color: "red" })
            setChatResults(s => {
                var index = s.findIndex(x => x.id == result.id)
                if (index >= 0) {
                    s[index].responseFail = true
                    s[index].responseFailMsg = JSON.stringify(error.response?.data)
                    s[index].responseLoading = false
                }
            })
        }
        setWaiting(false)
    }

    // 根據 mobile 產生唯一的 storage key
    const getStorageKey = (mobile: string) => `real_estate_context_${mobile}`;


    // 儲存 context 到 localStorage
    const saveContextToStorage = (mobile: string, contextData: ContextInputField) => {
        try {
            const key = getStorageKey(mobile);
            localStorage.setItem(key, JSON.stringify(contextData));
        } catch (error) {
            console.error('儲存 context 失敗:', error);
        }
    };

    // 根據 mobile 產生唯一的 used key
    const usedKey = `real_estate_used`;

    const saveUsedToStorage = (used: number) => {
        try {
            localStorage.setItem(usedKey, used.toString());
        } catch (error) {
            console.error('儲存 used 失敗:', error);
        }
    };

    // 從 localStorage 讀取 context
    const loadContextFromStorage = (mobile: string): ContextInputField | null => {
        try {
            const key = getStorageKey(mobile);
            const stored = localStorage.getItem(key);
            return stored ? JSON.parse(stored) : null;
        } catch (error) {
            console.error('讀取 context 失敗:', error);
            return null;
        }
    };

    const loadUsedStorage = (): number | null => {
        try {
            const stored = localStorage.getItem(usedKey);
            return stored ? Number(stored) : null;
        } catch (error) {
            console.error('讀取 context 失敗:', error);
            return null;
        }
    };

    // 當 baseContext 改變時，嘗試載入對應的 context
    useEffect(() => {
        const savedUsed = loadUsedStorage();
        setUsed(savedUsed ?? 0);
        if (savedUsed !== null && savedUsed >= limited) { setOpenhasReachedLimit(true); }
        if (baseContext?.mobile) {
            const savedContext = loadContextFromStorage(baseContext.mobile);

            if (savedContext) {
                setContext(savedContext);
                setOpened(false); // 如果有已存的資料，直接開始聊天
            } else {
                setOpened(true); // 沒有資料則開啟輸入表單
            }
        }
    }, [baseContext]);

    // 當 context 改變時，自動儲存到 localStorage
    useEffect(() => {
        if (context && baseContext?.mobile) {
            saveContextToStorage(baseContext.mobile, context);
        }
    }, [context, baseContext?.mobile]);

    // 修改 ContextInputPage 的 onChange
    const handleContextChange = (values: ContextInputField) => {
        setContext(values);
        setOpened(false);
        // 儲存會在 useEffect 中處理
    };

    // 清除特定用戶的 context 資料
    const clearUserContext = (mobile: string) => {
        try {
            const key = getStorageKey(mobile);
            localStorage.removeItem(key);
            //清除成功提示
            notifications.show({ message: "已清除資料", color: "green" });
        } catch (error) {
            console.error('清除 context 失敗:', error);
        }
    };

    const backToChat = () => {
        setStep(ChatStep.聊天);
    }

    const disabled = hasReachedLimit || waiting;

    return <>
        {step == ChatStep.基本資料 && <BaseInputPage onChange={(values) => { setBaseContext(values); }} onNext={() => { setStep(ChatStep.聊天); }} />}
        {step == ChatStep.聊天 && <>
            {context && <Modal
                opened={true}
                onClose={() => { }}
                withCloseButton={false}
                size="1280px"
                zIndex={200}
                styles={{
                    header: {
                        background: "#241E1E",
                    },
                    title: {
                        width: "100%",
                        display: "block"
                    },
                    content: {
                        borderRadius: '15px',
                        background: "transparent",
                        paddingLeft: "2rem",
                        paddingRight: "2rem"
                    },
                    body: {
                        padding: '2rem',
                        minHeight: 0
                    },
                }}
                withOverlay={false}
                title={
                    <Group justify="space-between" align="center" w="100%">
                        <div style={{ color: "#FAFAFA", fontWeight: 600 }}>
                            AI地產業務客服員 - 試用體驗
                        </div>
                        <Group>
                            <Button variant="subtle" color="#FFFFFF" bg="#A77145" style={{ fontWeight: "normal" }} onClick={() => {
                                setOpened(true);
                            }}>
                                修改房產資料
                            </Button>
                            <Button variant="outline" color="#B0B0B0" style={{ fontWeight: "normal" }} disabled={!baseContext?.mobile} onClick={() => {
                                if (baseContext?.mobile) clearUserContext(baseContext.mobile);
                            }}>
                                清除聊天資料
                            </Button>
                        </Group>
                    </Group>
                }
            >
                <Stack mih={0}>
                    <ScrollArea
                        viewportRef={viewport}
                        offsetScrollbars
                        h={'calc(90vh - 250px)'}
                        mah={'calc(90vh - 250px)'}
                    >
                        <Stack>
                            {chatResults.map((result, idx) => <Stack key={`chat_${idx}`} >
                                <ChatRequestCard chatModel={result} onReload={async () => {
                                    await EditResult(result)
                                }} onEditText={async t => {
                                    var r = R.clone(result)
                                    r.requestText = t
                                    await EditResult(r)
                                }} />
                            </Stack>)}
                        </Stack>
                    </ScrollArea>
                    <Group gap={5}>
                        <form style={{ flex: 1 }} onSubmit={form.onSubmit(async (values) => {
                            setWaiting
                            form.setFieldValue("text", "")
                            if (values.text) {
                                const id = uuidv4()
                                setChatResults(x => {
                                    x.push({
                                        id: id,
                                        requestText: values.text,
                                        response: {},
                                        responseLoading: true,
                                        responseFail: false,
                                    })
                                })
                                try {
                                    const start = Date.now();
                                    var contextString = Object.entries(context)
                                        .filter(([_, value]) => value !== '' && value !== 0)
                                        .map(([key, value]) => !!value ? `${key}: ${value}` : '')
                                        .join('\n');
                                    var response = await Chat_Mutation.mutateAsync({
                                        data: {
                                            message: values.text,
                                            records: GetRecords(),
                                            context: contextString
                                        }
                                    })
                                    console.log(response)
                                    const finish = Date.now()
                                    const time = (finish - start) / 1000
                                    setChatResults(x => {
                                        var index = x.findIndex(x => x.id == id)
                                        if (index >= 0) {
                                            x[index].response = response.data ?? {}
                                            x[index].responseTime = time
                                            x[index].responseLoading = false
                                        }
                                    })
                                    scrollToBottom()
                                    let newUsed = used + 1;
                                    setUsed(newUsed);
                                    saveUsedToStorage(newUsed);
                                } catch (e) {
                                    notifications.show({ message: "傳送失敗", color: "red" })
                                    setChatResults(x => {
                                        var error = e as AxiosError
                                        var index = x.findIndex(x => x.id == id)
                                        if (index >= 0) {
                                            x[index].responseFail = true
                                            x[index].responseFailMsg = JSON.stringify(error.response?.data)
                                            x[index].responseLoading = false
                                        }
                                    })
                                }
                            }
                            setWaiting(false)
                        })}>
                            <TextInput
                                placeholder="傳送訊息"
                                radius="lg"
                                disabled={disabled}
                                {...form.getInputProps('text')}
                                styles={{
                                    input: {
                                        height: '56px',
                                        minHeight: '56px'
                                    }
                                }}
                                rightSection={
                                    <Group gap={5} pr={5}>
                                        <ActionIcon
                                            type="submit"
                                            variant="filled"
                                            color="#A77145"
                                            size={"input-md"}
                                            bg="transparent"
                                            disabled={!form.values.text.trim() || disabled}
                                        >
                                            <IconSend2 size={28} />
                                        </ActionIcon>
                                    </Group>
                                }
                            />
                        </form>
                    </Group>
                    <Group justify="center">
                        <Text size="sm" c="#FFFFFF">
                            {!hasReachedLimit ? `問答數 餘${limited - used}` : "已達到試用問答上限，感謝您的試用！如有興趣，歡迎點擊右下LINE聯繫專員"}
                        </Text>
                    </Group>
                </Stack>
            </Modal>}
            <ContextInputPage onChange={handleContextChange} opened={opened} data={context} onCancel={() => setOpened(false)} baseData={baseContext} />
        </>
        }
        {step == ChatStep.回饋資料 && <FeedbackInputPage data={baseContext} backToChat={backToChat} />}
        {/*line button */}
        <Button variant="subtle" color="#FFFFFF" bg="#01B901" style={{
            fontWeight: "normal", position: "fixed", bottom: "125px", right: "20px", borderRadius: "50%",
            width: "88px", height: "88px", zIndex: 999
        }} onClick={() => {
            window.open("https://lin.ee/yrChoR0", '_blank');
        }}
            styles={{
                label: {
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center"
                }
            }}
        >
            <img src="/LINE_Brand_icon.png" alt="Line Icon" style={{ width: "38px", height: "38px" }} />
            <Text size="12px" c="#FFFFFF" >聯繫專員</Text>
        </Button>
        <Modal opened={openhasReachedLimit} onClose={() => { }} withCloseButton={false} size="md"
            zIndex={999}
            styles={{
                content: {
                    borderRadius: '15px',
                    background: " linear-gradient(180deg, #241E1E 0%, #09080E 100%)",
                    border: '2px solid #C09C54',
                    overflow: 'hidden',
                },
                body: {
                    padding: '2rem',
                    maxHeight: '90vh',
                    overflowY: 'auto', // 啟用垂直滾動
                    overflowX: 'hidden', // 隱藏水平滾動
                },
            }}
        >
            <Stack gap="sm" align="center">
                <Text c="#FAFAFA" size="lg" >
                    感謝您使用本服務，您的測試額度已達上限。
                    歡迎點擊右下角LINE聯繫專員進一步獲取更多產品資訊！
                </Text>
                <Button color='#A77145' style={{ fontWeight: "normal" }} onClick={() => {
                    setOpenhasReachedLimit(false);
                }}>
                    關閉
                </Button>
            </Stack>
        </Modal>
    </>
}